// The proxied Sluice CLI surface (`06` §3.10, §4, `SC-F700`–`SC-F715`):
// `config validate`, `policy validate`, `policy explain`, the stored test suites,
// `policy test`, the same runner over an **unsaved** draft (`SC-F708`) and over
// recorded queries (`SC-F715`).
//
// Everything here that runs a command is a **job** (`SC-F332`, frozen interface
// `I7`): the route answers `202` with `{job}` and a `Location` header, and the
// caller watches it with `watchJob` from `api/jobs.ts`. There is no second
// progress pattern in this product and this module deliberately does not invent
// one. `?wait=` collapses the fast cases — a `config.validate` that finishes in
// 1.5 s comes back terminal on the POST itself — so every starter here takes an
// optional `waitSeconds`.
//
// Two things this module exists to keep honest, because a client that
// re-derives them gets them wrong:
//
//   1. **A negative result is a SUCCEEDED job.** Exit 3 from `policy validate`
//      or `policy test` means the command ran correctly and the answer is no
//      (`06` §4.3). The job is `succeeded`, the HTTP call was `202`, and the
//      field to branch on is `result.ok` — never the job status and never the
//      HTTP status. Rendering exit 3 as a job failure buries the findings,
//      because a failed job carries an error envelope and no result.
//   2. **`policy test` exit 3 covers ZERO CASES RUN as well as case failures.**
//      [TestRunResult.reason] says which — `no_cases`, `case_failures` or
//      `bundle_invalid` — and a `no_cases` run is a FAILURE. A suite that
//      matched no files is exactly the failure mode that makes a test gate
//      worthless (`SC-S708`).
//
// **There is deliberately no live-execution function here.** `SC-F712` is the
// dry run and it is served by `POST …/explain`, which returns a decision and
// never a row. `POST /v1/instances/{id}/playground/query` (`SC-F713`) is v1
// behind `SC-F714`'s impersonation controls — a distinct feature grant absent
// from the default role set, a step-up grant, refusal for every scoped token
// and an audit record on every use — and `06` §3.10 keeps it out of the
// scoped-token route map *by absence*, so a `sluice_ft_` token is refused by
// there being no entry rather than by a check someone can forget to write. The
// capability does not exist on this build, so neither does a client function for
// it: `internal/clisurface` has no code path from any handler to a query. The
// distinction that makes the split defensible is worth restating —
// **explaining as a synthetic subject returns a decision; executing as a
// subject returns rows.**

import { del, getJSON, newIdempotencyKey, query, sendJSON, type Page } from "./http.js";
import type { JobStarted } from "./jobs.js";
import type { PolicyKind } from "./policies.js";

const base = (instanceId: string) => `/v1/instances/${encodeURIComponent(instanceId)}`;

/**
 * ValidationTarget names the bundle to run against: the draft, the live
 * generation, or a generation number from the history — which is what makes a
 * denial reproducible against the bundle that produced it (`SC-F809`).
 *
 * The wire field is a **string**, so a number is stringified by
 * [targetRef] before it is sent; `{"target": 7}` would not decode.
 */
export type ValidationTarget = "draft" | "live" | number;

/** targetRef is the wire spelling of a [ValidationTarget]. An omitted target
 * means `draft` server-side, which is what every authoring surface edits. */
export function targetRef(target: ValidationTarget | undefined): string | undefined {
  if (target === undefined) return undefined;
  return typeof target === "number" ? String(target) : target;
}

/**
 * TargetEcho is the resolved reference echoed on every proxied result.
 *
 * `digest` may be **empty**, and the emptiness is meaningful rather than
 * sloppy: a draft that has never been rendered has no digest yet, and a value
 * the control plane invented is one a CI job would assert against.
 */
export interface TargetEcho {
  /**
   * `unsaved` is `SC-F708`'s transient bundle — the stored draft with the
   * editor's unsaved state layered on top — and it is a distinct kind rather
   * than a flag because the distinction is the whole point: a report against
   * `draft` is about bytes that exist and can be filed against a generation, and
   * a report against `unsaved` is about bytes nobody can look up afterwards.
   */
  kind: "draft" | "live" | "generation" | "unsaved";
  /** The generation `seq` this resolved to; absent or 0 for an unrendered draft.
   * For `unsaved` it is the draft the unsaved state was layered ONTO, which is
   * the only stored thing such a run can name. */
  generation?: number;
  /** The full 64-character bundle digest, or empty when nothing was rendered. */
  digest?: string;
}

/**
 * SyntheticSubject is a subject invented for explain and for a test case.
 *
 * It is **always JWT-shaped and cannot be anything else**: `policytest.Identity`
 * carries no `authMethod` and no `apiKeyId`, the CLI exposes no flag for either,
 * and `buildUserCtx` hard-codes the JWT auth method — so a `subjects.apiKeys`
 * selector clause is unreachable in `policy explain` and `policy test` alike
 * (`SLUICE-U27`). An `api_key_id` is refused `422 validation_failed` naming
 * `SLUICE-U27` rather than accepted and ignored, because a field honoured
 * nowhere is how a caveat becomes a false result.
 *
 * Two shapes the CLI's own flag parsing forbids, refused by the API with a field
 * path so a form can highlight the input:
 *
 *   - a group containing a comma — `--groups` is CSV-split by `pflag`, so
 *     `a,b` would silently become two groups;
 *   - a claim value that is not a string, or a key containing `,` or `=` —
 *     `--claims` takes `key=value` text and parses nothing richer, so a number
 *     could not be delivered as a number.
 */
export interface SyntheticSubject {
  /** Required: explain evaluates exactly one named subject. */
  user: string;
  issuer?: string;
  email?: string;
  groups?: string[];
  /** Values must be strings — see the type doc. */
  claims?: Record<string, string>;
}

// ────────────────────────────────────────────────────────────── validate ──

/** ValidateCommand names a validator, not a CLI verb: `config` is
 * `sluice config validate <dir> --config <server.yaml> --strict`, `policy` is
 * `sluice policy validate <dir> --strict`. Omitting the field runs both, which
 * is `06` §4.4's own default and the right one for a pre-publish gate — neither
 * command reaches the other's stages. */
export type ValidateCommand = "config" | "policy";

/** ValidationSeverity is the two-value ladder the proxied validators produce.
 * The linter's richer ladder (`blocking-warning`, `info`, `hint`) belongs to
 * `SC-F529` and does not appear here. */
export type ValidationSeverity = "error" | "warning";

/** ValidationSource says which validator spoke. `SC-S704`'s third criterion is
 * that the linter and the two proxied validators share one finding shape and are
 * told apart by this field. */
export type ValidationSource = "sluice.config-validate" | "sluice.policy-validate";

/**
 * ValidationStage is how far the bundle got. The stages are **sequential and
 * mutually exclusive inside one command**: a decode failure returns before the
 * compiler runs, and a server-config failure returns before the policy directory
 * is read at all. So "no compile findings" can mean "the compiler never ran"
 * rather than "the compiler was happy", which is why
 * [ValidateResult.stage_reached] must be rendered next to the finding list.
 */
export type ValidationStage = "server_config" | "decode" | "compile" | "cross_check";

/**
 * ValidationFinding is one structured error from `config validate` or
 * `policy validate`, parsed out of line-based text (`SC-F703`) because neither
 * command has `--json` (`SLUICE-U03`).
 *
 * Three nullable fields are each a statement rather than an omission, and a UI
 * that papers over them points a reader at the wrong place:
 *
 *   - **`line` is ALWAYS null with the pinned binary.** The error format carries
 *     a line number and `internal/config` never populates it, so it is null and
 *     never 1. Anchor to the object, not to a line.
 *   - **`policy_id` is null when `(kind, name)` could not be recovered.** That
 *     is the compile stage: a compile error is one unstructured string with no
 *     file, no kind and no field path, so the finding anchors to the BUNDLE. A
 *     guessed id would open the wrong object's editor with confidence.
 *   - **`field` is empty at the compile stage by rule.** The paths inside a
 *     compile message are the ENGINE's (`resource.tags`), not the manifest's, so
 *     treating one as a form field would highlight an unrelated control.
 *
 * `raw` is always retained, so nothing the tool said is summarised away.
 */
export interface ValidationFinding {
  severity: ValidationSeverity;
  source: ValidationSource | string;
  stage: ValidationStage;
  /** Manifest path relative to the rendered policy directory, when known. */
  file?: string;
  kind?: PolicyKind | "";
  name?: string;
  policy_id: string | null;
  field: string | null;
  line: number | null;
  message: string;
  raw: string;
}

/**
 * ValidateResult is the merged report — one job `result` however many validators
 * ran (`06` §4.4).
 *
 * `ok` is the field a CI client branches on and it is false if **either**
 * validator reported anything. It is not the HTTP status: the call that created
 * the job answered `202` before the answer existed.
 */
export interface ValidateResult {
  ok: boolean;
  target: TargetEcho;
  sluice_version?: string;
  /** The validators that ran, in execution order (server config first). */
  commands: ValidateCommand[];
  stage_reached: ValidationStage;
  /**
   * Whether `config validate` printed `server config OK`. **Null means "not
   * checked"**, which is a different fact from `false`: a request for
   * `commands: ["policy"]` never loads a server config at all.
   */
  server_config_ok: boolean | null;
  /** Object count from `policies OK: <dir> (<n> objects, digest <hex>)`. */
  objects?: number;
  /**
   * The digest Sluice printed, which is the **short form**: `shortDigest`
   * truncates to 12 characters, so this is a PREFIX to compare against the first
   * 12 characters of [TargetEcho.digest], never a full digest.
   */
  reported_digest_prefix?: string;
  findings: ValidationFinding[];
  counts: Record<ValidationSeverity, number>;
  /** Output lines the grammar did not recognise, verbatim. Show them: a parser
   * that reported "no errors" on output it did not understand would turn a
   * validation failure into a green light (`SC-S704`). */
  unparsed?: string[];
  /** Conditions a reader needs in order to interpret the report — that
   * `--strict` is stricter than the running instance, or that Sluice's own error
   * count disagreed with the number of findings this build parsed. */
  notes?: string[];
  /** The `cli_runs` rows, one per command, for a reader who wants the raw
   * streams behind a report they do not trust. */
  run_ids?: string[];
  duration_ms: number;
}

/**
 * validate runs `config validate` and `policy validate` over a target
 * (`SC-F701`, `SC-F702`) → job. Read-only: it does not take the instance's
 * serialization lock, so it never answers `instance_busy`.
 *
 * `commands` defaults to both. A duplicate entry is refused rather than
 * deduplicated, because `["policy","policy"]` and `["policy"]` describe one
 * operation and two idempotency hashes.
 */
export function validate(
  instanceId: string,
  input: { target?: ValidationTarget; commands?: ValidateCommand[] } = {},
  opts: { waitSeconds?: number } = {},
): Promise<JobStarted<ValidateResult>> {
  return sendJSON<JobStarted<ValidateResult>>(
    `${base(instanceId)}/validate${query({ wait: opts.waitSeconds })}`,
    "POST",
    { target: targetRef(input.target), commands: input.commands },
    { idempotencyKey: newIdempotencyKey() },
  );
}

// ─────────────────────────────────────────────────────────────── explain ──

/** ExplainInput asks what policy would do. Exactly one of `sql` or `table` is
 * required and `subject.user` is required — the CLI enforces both, and sending
 * neither or both is one refusal because the remedy is the same. `table` must be
 * a three-part `catalog.schema.table` name. */
export interface ExplainInput {
  target?: ValidationTarget;
  subject: SyntheticSubject;
  sql?: string;
  table?: string;
}

/**
 * DecisionKind is the honest reading of an effective decision, derived
 * server-side.
 *
 * The split inside `deny` is the whole point: `no_policy_allowed` is the
 * engine's abstained default-deny — **nothing allowed this** — and
 * `denied_by_policy` is a policy that matched and refused. The two have
 * different fixes, so they must never share a label (`SC-S705`). The distinction
 * is an inference from an empty `rejected` list, because the engine's own
 * `Abstained` flag is not part of the explain payload; the caveat block says so.
 */
export type DecisionKind =
  | "allow"
  | "denied_by_policy"
  | "no_policy_allowed"
  | "rejected"
  | "unknown";

/** AppliedPolicy names a policy whose selector matched. `policy_id` is the
 * control-plane record, so a decision can be drilled through to the object that
 * produced it (`SC-F809`); it is null when the object is not in this draft. */
export interface AppliedPolicy {
  kind: PolicyKind | string;
  name: string;
  priority: number;
  policy_id: string | null;
}

/** RejectedPolicy names a policy that matched and refused, with its reason
 * verbatim. */
export interface RejectedPolicy {
  kind: PolicyKind | string;
  name: string;
  reason: string;
  policy_id: string | null;
}

/** ColumnMaskRef is a mask that would be applied. `column` is the fully
 * qualified `table.column`, because a bare column name would be ambiguous across
 * a join. **There are no mask arguments here** — the payload does not carry
 * them, so the UI cannot show what the mask would produce. */
export interface ColumnMaskRef {
  column: string;
  mask_type: string;
  policy: string;
}

/**
 * EffectiveDecision is the resolution of every applied policy.
 *
 * `row_filters` is a **set of table keys that carry a filter** — not predicates,
 * and not an order. It comes from a map iteration upstream and is sorted only so
 * that two identical explains produce identical bytes; the sort adds no meaning.
 * Render it as an unordered set, never as a numbered sequence.
 */
export interface EffectiveDecision {
  decision: string;
  row_filters: string[];
  column_masks: ColumnMaskRef[];
}

/** ExplainedDecision is `apitypes.ExplainResult` as this API re-publishes it.
 * `shadow` is policies that matched while running in `Audit` or `DryRun`, so they
 * did **not** affect the decision — presenting them beside `matched` without the
 * distinction would make a not-yet-enforced policy look enforced. */
export interface ExplainedDecision {
  subject: string;
  resource: string;
  decision_kind: DecisionKind;
  matched: AppliedPolicy[];
  shadow: AppliedPolicy[];
  rejected: RejectedPolicy[];
  approval_required: AppliedPolicy[];
  effective: EffectiveDecision;
}

/** DryRunDisclosure is `SC-F712`'s block, and every field is a negative: no
 * statement is executed, no datasource is contacted, and `rows_returned` is an
 * explicit zero rather than an absent field a client might read as unknown.
 * `notice` is the sentence to render beside the result. */
export interface DryRunDisclosure {
  dry_run: boolean;
  executed: boolean;
  rows_returned: number;
  notice: string;
}

/**
 * ExplainResult is the `policy.explain` job result (`06` §4.5).
 *
 * **Render `caveats`.** They are not filler and they are not the client's to
 * summarise: explain returns no composed predicate text, no mask arguments and
 * no rewrite effect; `effective.row_filters` is unordered; explain never states
 * *why* a policy failed to match; the simulated subject is always JWT-shaped so
 * API-key subject semantics cannot be simulated (`SLUICE-U27`); and the
 * abstained/explicit deny split is an inference. A screen that hides them
 * promises an answer the tool did not give (`SC-F704`, concept principle 8).
 *
 * `ok` is false only for a statement that did not parse — `parse_error` then
 * carries the parser's own message. That is a SQL syntax error shown against the
 * statement, **not** a policy result and not a denial (`SC-S714`).
 */
export interface ExplainResult {
  ok: boolean;
  target: TargetEcho;
  sluice_version?: string;
  explain: ExplainedDecision | null;
  parse_error: string | null;
  caveats: string[];
  dry_run: DryRunDisclosure;
  run_id?: string;
  duration_ms: number;
}

/**
 * explain runs `policy explain` with a synthetic subject (`SC-F704`, `SC-F712`)
 * → job. It is `member`-reachable and therefore usable by automation, because it
 * is a metadata operation over policy the caller can already read.
 *
 * The matrix form of `06` §4.5.1 (`subjects[]` × `targets[]`) is **not
 * available** on this build and is refused with a message saying so: the pinned
 * CLI evaluates one subject and one target per invocation, and looping
 * client-side would produce a partial answer that looks complete.
 */
export function explain(
  instanceId: string,
  input: ExplainInput,
  opts: { waitSeconds?: number } = {},
): Promise<JobStarted<ExplainResult>> {
  return sendJSON<JobStarted<ExplainResult>>(
    `${base(instanceId)}/explain${query({ wait: opts.waitSeconds })}`,
    "POST",
    {
      target: targetRef(input.target),
      subject: input.subject,
      sql: input.sql || undefined,
      table: input.table || undefined,
    },
    { idempotencyKey: newIdempotencyKey() },
  );
}

/**
 * EXPLAIN_PREFILL is the query-string contract three surfaces share: the test
 * report's "explain this case" action, the playground's "open in explain", and
 * an audit record's reproduce action all hand `<page-explain>` a subject and a
 * statement. It is one spelling in one place so the three cannot disagree.
 *
 * `claim` repeats as `claim=<key>=<value>`, split on the FIRST `=`. That is
 * lossless here because the API already refuses a claim key containing `=` and
 * any claim value containing a comma.
 */
export const EXPLAIN_PREFILL = {
  target: "target",
  user: "subject",
  issuer: "issuer",
  email: "email",
  /** Repeated, one per group — never comma-joined, because a group may not
   * contain a comma and joining would invent the very ambiguity the API
   * refuses. */
  group: "group",
  claim: "claim",
  sql: "sql",
  table: "table",
} as const;

/** explainPrefill builds the query string [parseExplainPrefill] reads. */
export function explainPrefill(input: ExplainInput): string {
  const params = new URLSearchParams();
  const ref = targetRef(input.target);
  if (ref) params.set(EXPLAIN_PREFILL.target, ref);
  if (input.subject.user) params.set(EXPLAIN_PREFILL.user, input.subject.user);
  if (input.subject.issuer) params.set(EXPLAIN_PREFILL.issuer, input.subject.issuer);
  if (input.subject.email) params.set(EXPLAIN_PREFILL.email, input.subject.email);
  for (const g of input.subject.groups ?? []) params.append(EXPLAIN_PREFILL.group, g);
  for (const [k, v] of Object.entries(input.subject.claims ?? {})) {
    params.append(EXPLAIN_PREFILL.claim, `${k}=${v}`);
  }
  if (input.sql) params.set(EXPLAIN_PREFILL.sql, input.sql);
  if (input.table) params.set(EXPLAIN_PREFILL.table, input.table);
  const s = params.toString();
  return s ? `?${s}` : "";
}

/** parseExplainPrefill reads [explainPrefill]'s query string back. An absent
 * parameter leaves the field unset rather than empty, so a page can tell "not
 * prefilled" from "prefilled with nothing". */
export function parseExplainPrefill(search: string): {
  target?: ValidationTarget;
  subject: SyntheticSubject;
  sql?: string;
  table?: string;
} {
  const p = new URLSearchParams(search);
  const subject: SyntheticSubject = { user: p.get(EXPLAIN_PREFILL.user) ?? "" };
  const issuer = p.get(EXPLAIN_PREFILL.issuer);
  if (issuer) subject.issuer = issuer;
  const email = p.get(EXPLAIN_PREFILL.email);
  if (email) subject.email = email;
  const groups = p.getAll(EXPLAIN_PREFILL.group).filter((g) => g !== "");
  if (groups.length > 0) subject.groups = groups;
  const claims: Record<string, string> = {};
  for (const raw of p.getAll(EXPLAIN_PREFILL.claim)) {
    const at = raw.indexOf("=");
    if (at <= 0) continue;
    claims[raw.slice(0, at)] = raw.slice(at + 1);
  }
  if (Object.keys(claims).length > 0) subject.claims = claims;

  const out: { target?: ValidationTarget; subject: SyntheticSubject; sql?: string; table?: string } =
    { subject };
  const target = p.get(EXPLAIN_PREFILL.target);
  if (target === "draft" || target === "live") out.target = target;
  else if (target && /^[0-9]+$/.test(target)) out.target = Number(target);
  const sql = p.get(EXPLAIN_PREFILL.sql);
  if (sql) out.sql = sql;
  const table = p.get(EXPLAIN_PREFILL.table);
  if (table) out.table = table;
  return out;
}

// ─────────────────────────────────────────────────────────── test suites ──

/**
 * CaseIdentity is the subset of the gateway's user context a case can specify.
 * It is JWT-shaped for the same reason [SyntheticSubject] is, and an
 * `api_key_id` is refused `422` rather than silently ignored: a suite that
 * appeared to cover the API-key path and did not is worse than one that admits
 * it cannot (`SLUICE-U27`).
 */
export interface CaseIdentity {
  subject: string;
  issuer?: string;
  email?: string;
  groups?: string[];
  /** Values must be strings — a `SubjectBinding` delivers claims as strings, so
   * a richer type would test a value the live path cannot produce. */
  claims?: Record<string, string>;
}

/**
 * CaseRequest carries per-request facts, and setting it is **the trap this whole
 * surface warns about**: the test harness populates request facts while the live
 * REST and MCP paths do not (`SLUICE-U12`), so a case that depends on them
 * passes here and the same policies fail in production — and they fail as a
 * DENIAL, because a `{{ request.* }}` template that cannot render denies the
 * query.
 *
 * It is accepted rather than refused, because an imported suite may legitimately
 * carry one and refusing would make the product unable to run a suite it can
 * read. Every run over such a suite carries the warning, and the affected cases
 * are flagged individually (`SC-S709`).
 */
export interface CaseRequest {
  headers?: Record<string, string>;
  userAgent?: string;
}

/**
 * CaseExpect is the assertion set. An **empty field is not checked**, which is
 * the runner's own rule — so an expectation nobody filled in is not a passing
 * assertion, it is no assertion.
 *
 * The five list fields compare as **sorted sets** and `rewrittenSql` compares
 * **whitespace-normalised**. Both semantics happen inside the pinned binary and
 * are reproduced in the diff rather than in a second comparison here.
 */
export interface CaseExpect {
  outcome?: TestOutcome;
  denyPolicy?: string;
  errorCode?: string;
  rejections?: string[];
  filters?: string[];
  masks?: string[];
  postMasks?: string[];
  applied?: string[];
  rewrittenSqlContains?: string[];
  rewrittenSql?: string;
}

/**
 * TestOutcome is the closed set `policytest` compares `expect.outcome` against.
 *
 * It is stated here rather than read from the form schema because the schema
 * describes the eleven **policy** kinds and this is the `policy test` dialect's
 * own vocabulary, validated by `internal/clisurface`'s `expectOutcomes`. A typo
 * — `denied` for `deny` — produces a case that fails with
 * `outcome: want "denied" got "deny"`, which reads as a policy problem and is
 * not one, so the form offers the closed set and the route refuses anything
 * else.
 */
export type TestOutcome = "allow" | "deny" | "reject";

/** TEST_OUTCOMES is [TestOutcome] as a list, for a picker. */
export const TEST_OUTCOMES: readonly TestOutcome[] = ["allow", "deny", "reject"];

/** SuiteCase is one scenario: an identity, an optional request block, a
 * statement and the assertions. */
export interface SuiteCase {
  name: string;
  identity: CaseIdentity;
  request?: CaseRequest;
  sql: string;
  expect: CaseExpect;
}

/**
 * TestSuite is one authored `policy test` document (`SC-F706`).
 *
 * The control plane **renders** it into `policy test`'s own YAML dialect — no
 * `apiVersion`, camelCase keys, a `schema:` fixture map — so the customer never
 * writes that dialect and it stays an implementation detail. The rendered file
 * lands under `policies.d/tests/`, the one path Sluice's loader skips, so a
 * suite does not perturb the bundle digest or the publish confirmation.
 *
 * `cases` must be non-empty. An empty suite is refused at authoring time rather
 * than discovered at publish, because `policy test` exits 3 on zero cases run:
 * an empty suite publishes as a failing gate nobody asked for.
 */
export interface TestSuite {
  /** Absent on create. */
  id?: string;
  /** 1–63 characters of lower-case letters, digits and hyphens: the name
   * becomes a file name inside the rendered bundle. */
  name: string;
  /** Three-part `catalog.schema.table` names to column lists, for `SELECT *`
   * expansion with no live datasource. Auto-populated from the schema cache
   * (`SC-F411`) rather than typed. */
  schema?: Record<string, string[]>;
  cases: SuiteCase[];
  /** Whether `SC-F709`'s opt-in pre-publish test gate runs this suite. */
  enabled: boolean;
  created?: string;
  updated?: string;
}

/** listTestSuites returns the instance's stored suites (`SC-F706`), viewer+.
 * Not paginated in practice — a suite is a document whose name is a file name in
 * one bundle, so there are a handful — but the `{items, next}` envelope is
 * carried with a null `next` so a client written against pagination works. */
export function listTestSuites(instanceId: string): Promise<Page<TestSuite>> {
  return getJSON<Page<TestSuite>>(`${base(instanceId)}/tests/suites`);
}

/** createTestSuite stores a suite (`SC-F706`), maintainer+. A duplicate name is
 * `409 name_taken` from the unique index. */
export function createTestSuite(instanceId: string, suite: TestSuite): Promise<TestSuite> {
  return sendJSON<TestSuite>(`${base(instanceId)}/tests/suites`, "POST", suite, {
    idempotencyKey: newIdempotencyKey(),
  });
}

/** replaceTestSuite replaces a stored suite whole, maintainer+. It is a replace
 * and not a merge because a suite is one document and a PATCH over an array of
 * cases has no semantics a client could rely on. */
export function replaceTestSuite(
  instanceId: string,
  suiteId: string,
  suite: TestSuite,
): Promise<TestSuite> {
  return sendJSON<TestSuite>(
    `${base(instanceId)}/tests/suites/${encodeURIComponent(suiteId)}`,
    "PUT",
    suite,
  );
}

/** deleteTestSuite removes a stored suite, maintainer+. Its stored reports
 * cascade with it — a report about a deleted suite names cases nobody can look
 * up — while the `cli_runs` evidence of what actually ran survives. */
export function deleteTestSuite(instanceId: string, suiteId: string): Promise<void> {
  return del(`${base(instanceId)}/tests/suites/${encodeURIComponent(suiteId)}`);
}

// ───────────────────────────────────────────────────────────── test runs ──

/**
 * TestReason says why a run is not a pass. Three conditions arrive from the
 * binary as one exit code and this field keeps them apart, because they have
 * three different remedies:
 *
 *   - `case_failures` — cases ran and some failed; fix the policies or the
 *     expectations.
 *   - `no_cases` — the suite ran **zero** cases. This is a FAILURE, not an empty
 *     pass, and `SC-F709`'s gate reads it.
 *   - `bundle_invalid` — the policy directory did not load or compile, so
 *     nothing was tested; the bundle-level findings say what.
 */
export type TestReason = "no_cases" | "case_failures" | "bundle_invalid";

/** Assertion is the label a failed comparison belongs to. `unparsed` is the
 * honest degradation: the runner emits preformatted human strings, so when a
 * future Sluice version rewords one the parser keeps `raw` and says it could not
 * structure it, rather than inventing a shape. */
export type Assertion =
  | "evaluate"
  | "outcome"
  | "denyPolicy"
  | "errorCode"
  | "parse"
  | "rewrite"
  | "rewrittenSqlContains"
  | "rewrittenSql"
  | "rejections"
  | "filters"
  | "masks"
  | "postMasks"
  | "applied"
  | "unparsed";

/**
 * Comparison is how the runner compared the two sides, and it is the field a
 * diff view must render — not the assertion label — because the comparison is
 * what is counter-intuitive:
 *
 *   - `sorted_set` — both sides are sorted and joined before comparing, so order
 *     is irrelevant and a missing element and an extra element are both
 *     failures. Never say "partially matched".
 *   - `whitespace_normalized` / `substring` — both sides pass through whitespace
 *     normalisation first, so indentation is visibly not the cause.
 */
export type Comparison =
  | "equality"
  | "sorted_set"
  | "whitespace_normalized"
  | "substring"
  | "error"
  | "none";

/**
 * AssertionDiff is one failed assertion.
 *
 * `raw` is always retained. Every structured field here is a parse of text that
 * was never meant to be parsed, so the raw line is the only thing guaranteed to
 * be exactly what the runner said (`SLUICE-U25`).
 */
export interface AssertionDiff {
  assertion: Assertion | string;
  comparison: Comparison | string;
  /** The scalar sides, unquoted. */
  want?: string;
  got?: string;
  /** The one shape with no observed value at all: the rewrite succeeded, so
   * there was no error code — which is not the same as an empty code. */
  got_absent?: boolean;
  want_list?: string[];
  got_list?: string[];
  /** Multiset differences: in `want_list` and not in `got_list`, and the
   * reverse. */
  missing?: string[];
  unexpected?: string[];
  /** The whitespace-normalised forms the runner actually compared. */
  normalized_want?: string;
  normalized_got?: string;
  /** The comparison's semantics in prose, generated control-plane-side from the
   * same knowledge the linter encodes. */
  hint?: string;
  raw: string;
}

/** TestCaseResult is one case's outcome. `uses_request_facts` flags a case that
 * sets a `request` block, so the report can badge THAT case rather than only
 * banner the run (`SC-S709`). */
export interface TestCaseResult {
  name: string;
  passed: boolean;
  diffs?: AssertionDiff[];
  uses_request_facts?: boolean;
}

/**
 * TestRunResult is the `policy.test` job result (`06` §4.6).
 *
 * **`total: 0` with `ok: false` and `reason: "no_cases"` is a red result, not a
 * missing one.** Retention is 90 days rather than 30 because a test run is
 * evidence: a publish can be asked which suite result gated it.
 *
 * `warnings` is the request-facts asymmetry (`SLUICE-U12`) and must be rendered
 * where it cannot be missed; on a **passing** run it also carries the
 * annotation that the pass is qualified, because silence over a green run is
 * read as confidence.
 */
export interface TestRunResult {
  ok: boolean;
  reason?: TestReason;
  target: TargetEcho;
  sluice_version?: string;
  /** Empty for an inline suite, which has no stored history to hang a report on. */
  suite_id?: string;
  suite_name: string;
  total: number;
  passed: number;
  failed: number;
  cases: TestCaseResult[];
  /** Bundle-level errors when the directory did not load — the same shape a
   * validate report uses, because it is the same loader reached through a
   * different command. */
  findings?: ValidationFinding[];
  warnings?: string[];
  /** The `SLUICE-U27` limitation: a case's identity is JWT-shaped exactly as an
   * explain's subject is, so a suite that appeared to cover the API-key path
   * does not. */
  caveats: string[];
  /** Present **only** when the run evaluated a transient bundle (`SC-F708`). Its
   * presence is the signal to branch on: with it, this report is about bytes that
   * were never saved and must not be shown as evidence about a generation. */
  unsaved?: UnsavedBundle;
  /** Present **only** when the suite was generated from recorded queries
   * (`SC-F715`). */
  replay?: ReplayReport;
  run_id?: string;
  test_run_id?: string;
  duration_ms: number;
}

// ───────────────────────────────────── SC-F708 · the unsaved draft ──

/**
 * ObjectRef addresses one policy object by `(kind, name)`, which is the only
 * coordinate a client holds before an object has been saved.
 */
export interface ObjectRef {
  kind: PolicyKind;
  name: string;
}

/**
 * DraftOverlay is the editor's **unsaved** state, expressed as a difference from
 * the stored draft (`SC-F708`).
 *
 * It is an overlay and not a whole bundle on purpose. A client that sent the
 * complete policy set would become authoritative for what gets tested, so a
 * stale editor tab would silently test a bundle that is *not* the draft plus its
 * own edits — which is the one thing this feature must not do, because the result
 * is read as "my change is safe". The base is read server-side from the stored
 * draft at the moment the run starts.
 *
 * An overlay object is always rendered; there is no `enabled` flag, because
 * disabled and absent are the same thing to the renderer. To model an object you
 * have turned off, list it in `omit`.
 */
export interface DraftOverlay {
  /** The unsaved documents, each the same `{kind, metadata, spec}` value
   * `POST …/policies` takes. An overlay object replaces the stored object with
   * the same `(kind, name)` and is added when there is none — which makes an
   * unsaved create and an unsaved edit the same request. At most 32. */
  objects?: unknown[];
  /** The unsaved deletes and disables. A ref that matches nothing in the draft is
   * refused rather than ignored: a caller omitting an object that is not there is
   * working from a stale view. */
  omit?: ObjectRef[];
}

/**
 * UnsavedBundle describes the transient bundle a `SC-F708` run evaluated.
 *
 * Every count is computed **after** the merge, server-side, rather than echoed
 * from the request — so an object you meant to create appearing under `replaced`
 * is how you learn your editor was stale, instead of that difference being
 * folded away.
 */
export interface UnsavedBundle {
  /** The `seq` of the stored draft the unsaved state was layered onto. */
  based_on: number;
  /** The transient bundle's digest: what an instance would report for this
   * content if it were published. It names no stored generation. */
  digest: string;
  added: ObjectRef[];
  replaced: ObjectRef[];
  omitted: ObjectRef[];
  /** Always `false`. Present so a client has a boolean to branch on rather than
   * inferring the fact from this block's presence. */
  persisted: boolean;
  /** The sentence to render beside the verdict. */
  notice: string;
}

// ────────────────────────────────────────── SC-F715 · query replay ──

/** ReplayVerdict is what a candidate policy set does with one recorded query.
 *
 * `inconclusive` is the value that must never be rendered as a change: the case
 * failed on an `evaluate`, `rewrite` or `parse` assertion, so the engine never
 * reached a comparable decision. The usual cause is a `SELECT *` that could not
 * expand, because a generated suite ships no schema fixture — an audit record
 * names the tables a query touched and not their columns. */
export type ReplayVerdict = "unchanged" | "changed" | "inconclusive" | "skipped" | "unknown";

/** ReplaySkipReason says why a recorded query was not replayed. `api_key_subject`
 * is the one that matters most on a hosted deployment: it is `SLUICE-U27`, and it
 * is reported and counted rather than folded into `unchanged`. */
export type ReplaySkipReason =
  | "api_key_subject"
  | "not_a_subject_query"
  | "no_recorded_sql"
  | "no_recorded_subject"
  | "recorded_decision_not_comparable"
  | "over_the_scenario_cap";

/** ReplayScenario is one deduplicated recorded query and its verdict. `case`
 * joins it to the entry in [TestRunResult.cases] and is empty for a skipped one. */
export interface ReplayScenario {
  case?: string;
  verdict: ReplayVerdict;
  skip_reason?: ReplaySkipReason | string;
  subject: string;
  issuer?: string;
  groups?: string[];
  auth_method?: string;
  sql?: string;
  sql_truncated?: boolean;
  tables?: string[];
  recorded_decision: string;
  recorded_error_code?: string;
  /** What the candidate decided, when the case produced a comparable answer.
   * Empty for `unchanged` too — a passing case asserts equality and reports no
   * observed value — so read `recorded_decision` for those. */
  replayed_decision?: string;
  /** How many records collapsed into this scenario. A change over 4 000
   * occurrences is a different-sized problem from a change over one. */
  occurrences: number;
  first_seen: string;
  last_seen: string;
  /** The newest contributing record, so the audit drawer is one click away. */
  record_id?: string;
  /** The raw assertion lines behind a `changed` or `inconclusive` verdict. */
  failures?: string[];
}

/** ReplayCounts is the arithmetic of one replay. The interesting numbers are the
 * denominators: `skipped_by_reason` is what keeps the API-key limitation from
 * being an abstract warning. */
export interface ReplayCounts {
  records_scanned: number;
  scenarios_found: number;
  replayed: number;
  skipped: number;
  unchanged: number;
  changed: number;
  inconclusive: number;
  skipped_by_reason?: Record<string, number>;
}

/**
 * ReplayReport is `SC-F715`'s block on the `policy.test` job result.
 *
 * It hangs off [TestRunResult] rather than being a result type of its own because
 * a replay **is** a `policy test` run: the job's type decides its result schema,
 * and a second schema under one type is how a client that branches on `type`
 * starts guessing.
 */
export interface ReplayReport {
  from: string;
  to: string;
  /** The policy set that was asked about. */
  candidate: TargetEcho;
  /** What it was compared against — the recorded decision, not a second render. */
  baseline: string;
  cap: number;
  truncated: boolean;
  /** The ordering rule in prose, because which scenarios survived a truncation is
   * not inferable from the surviving list. */
  selection: string;
  /** Always `false` / `0`: a replay re-decides a statement and executes nothing. */
  executed: boolean;
  rows_returned: number;
  /** The honest limits. **Render them**: they are facts about the pinned build
   * and about the bound, not disclaimers. */
  limits: string[];
  counts: ReplayCounts;
  scenarios: ReplayScenario[];
}

/** ReplayInput is `POST …/audit/replays`' body. Omitting the range means the
 * trailing 24 hours; the window may span at most 7 days, and `limit` at most 200. */
export interface ReplayInput {
  target?: ValidationTarget;
  from?: string;
  to?: string;
  limit?: number;
}

/**
 * runTests runs `policy test` over an inline or a stored suite (`SC-F707`) →
 * job. Send `suite_id` for a stored suite or `suite` inline — never both;
 * sending both is refused rather than resolved by precedence.
 *
 * Send `draft` to run against your **unsaved** changes (`SC-F708`): the stored
 * draft is read, your objects are layered onto it, and the rendered result is
 * mounted for this one run. `target` must then be the draft — `live` and a
 * numbered generation are published bundles and have no unsaved state — and
 * nothing is persisted, so `test_run_id` comes back empty and the report lives on
 * the job.
 *
 * A `422 validation_failed` with `details.reason = "no_test_suite"` is not a
 * test failure: it is exit 1 because the tests path does not exist, which means
 * an instance with no suite. `SC-S708` requires it to be distinguishable and to
 * offer to create one.
 */
export function runTests(
  instanceId: string,
  input: {
    target?: ValidationTarget;
    suite?: TestSuite;
    suite_id?: string;
    draft?: DraftOverlay;
  },
  opts: { waitSeconds?: number } = {},
): Promise<JobStarted<TestRunResult>> {
  return sendJSON<JobStarted<TestRunResult>>(
    `${base(instanceId)}/tests/runs${query({ wait: opts.waitSeconds })}`,
    "POST",
    {
      target: targetRef(input.target),
      suite: input.suite,
      suite_id: input.suite_id,
      draft: input.draft,
    },
    { idempotencyKey: newIdempotencyKey() },
  );
}

/**
 * replayQueries generates a `policy test` suite from the recorded queries in a
 * window and runs it once against a candidate policy set (`SC-F715`) → job.
 *
 * The job's type is `policy.test` and its result is a [TestRunResult] whose
 * `replay` block carries the provenance: a case that fails its `outcome`
 * assertion is a query the candidate decides differently.
 *
 * Requires **maintainer and a session or a general API key**. A replay reads
 * recorded SQL, subject ids, emails and groups, and `06` §3.12.1 restricts those
 * to that exact combination — so a fine-grained token is refused, and the route
 * carries no scoped-token entry at all.
 *
 * A window with no replayable record is a `422`, not an empty all-clear: the
 * details carry `records_scanned`, `scenarios_found` and `skipped_by_reason`, so
 * "your window is empty" and "your traffic is all API-key traffic and this build
 * cannot model it" are distinguishable.
 */
export function replayQueries(
  instanceId: string,
  input: ReplayInput = {},
  opts: { waitSeconds?: number } = {},
): Promise<JobStarted<TestRunResult>> {
  return sendJSON<JobStarted<TestRunResult>>(
    `${base(instanceId)}/audit/replays${query({ wait: opts.waitSeconds })}`,
    "POST",
    { target: targetRef(input.target), from: input.from, to: input.to, limit: input.limit },
    { idempotencyKey: newIdempotencyKey() },
  );
}
